<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import ColorInput from 'dashboard/components-next/colorpicker/ColorInput.vue';

const props = defineProps({
  feedbackEnabled: { type: Boolean, default: false },
  inboxId: { type: [String, Number], default: '' },
  coverPreviewSource: { type: String, default: '' },
  coverPreviewBgColor: { type: String, default: '' },
  thumbnailUrl: { type: String, default: '' },
  templateId: { type: String, default: '' },
  selectedEventId: { type: [String, Number], default: null },
  themeAccentColor: { type: String, default: '#7c3aed' },
});

const emit = defineEmits([
  'update:feedbackEnabled',
  'update:inboxId',
  'update:coverPreviewSource',
  'update:coverPreviewBgColor',
  'update:thumbnailUrl',
]);

const { t } = useI18n();
const store = useStore();
const generating = ref(false);

const themeAccent = computed(() => props.themeAccentColor || '#7c3aed');

const usingThemeColor = computed(() => !props.coverPreviewBgColor);

const effectiveBgColor = computed(
  () => props.coverPreviewBgColor || themeAccent.value
);

const inboxOptions = computed(() => {
  const inboxes = store.getters['inboxes/getWebsiteInboxes'] || [];
  const widgets = inboxes.map(inbox => ({
    value: String(inbox.id),
    label: inbox.name,
  }));
  return [
    {
      value: '',
      label: t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.WIDGET_NONE'),
    },
    ...widgets,
  ];
});

const selectedInbox = computed({
  get: () => (props.inboxId == null ? '' : String(props.inboxId)),
  set: value => emit('update:inboxId', value),
});

const onBgColorChange = value => {
  const next = (value || '').trim();
  if (!next || next.toLowerCase() === themeAccent.value.toLowerCase()) {
    emit('update:coverPreviewBgColor', '');
    return;
  }
  emit('update:coverPreviewBgColor', next.startsWith('#') ? next : `#${next}`);
};

const useThemeColor = () => {
  emit('update:coverPreviewBgColor', '');
};

const regenerateCover = async () => {
  if (!props.templateId || generating.value) return;
  generating.value = true;
  try {
    const data = await store.dispatch(
      'planningEventPortals/generateThumbnail',
      {
        templateId: props.templateId,
        eventId: props.selectedEventId,
        source: 'generated',
        bgColor: effectiveBgColor.value,
        useThemeBg: usingThemeColor.value,
      }
    );
    if (data?.thumbnail_url) {
      emit(
        'update:thumbnailUrl',
        `${data.thumbnail_url}${data.thumbnail_url.includes('?') ? '&' : '?'}t=${Date.now()}`
      );
      emit('update:coverPreviewSource', 'generated');
      if (data.cover_preview_bg_color != null) {
        emit('update:coverPreviewBgColor', data.cover_preview_bg_color || '');
      }
    }
  } finally {
    generating.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-5 p-3">
    <section class="flex flex-col gap-2.5">
      <div>
        <h4 class="text-xs font-semibold text-n-slate-12">
          {{ t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.COVER_TITLE') }}
        </h4>
        <p class="text-[11px] text-n-slate-11 mt-0.5 leading-relaxed">
          {{ t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.COVER_HINT') }}
        </p>
      </div>
      <div
        class="overflow-hidden rounded-lg border border-n-slate-5 bg-n-slate-2 aspect-[1200/630]"
      >
        <img
          v-if="thumbnailUrl"
          :src="thumbnailUrl"
          alt=""
          class="w-full h-full object-cover"
        />
        <div
          v-else
          class="flex h-full items-center justify-center px-3 text-center text-[11px] text-n-slate-9"
        >
          {{ t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.COVER_EMPTY') }}
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <div class="flex items-center justify-between gap-2">
          <label class="text-[11px] font-medium text-n-slate-11">
            {{ t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.COVER_BG_COLOR') }}
          </label>
          <button
            v-if="!usingThemeColor"
            type="button"
            class="text-[10px] font-medium text-n-brand-11 hover:underline"
            @click="useThemeColor"
          >
            {{ t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.COVER_USE_THEME') }}
          </button>
          <span v-else class="text-[10px] text-n-slate-9">
            {{
              t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.COVER_THEME_DEFAULT')
            }}
          </span>
        </div>
        <ColorInput
          :model-value="effectiveBgColor"
          @update:model-value="onBgColorChange"
        />
      </div>

      <button
        type="button"
        class="flex items-center justify-center gap-1.5 w-full rounded-lg border border-n-slate-5 bg-n-slate-1 px-3 py-2 text-xs font-medium text-n-slate-12 hover:bg-n-slate-2 transition-colors disabled:opacity-50"
        :disabled="generating"
        @click="regenerateCover"
      >
        <span
          class="size-3.5"
          :class="[
            generating
              ? 'i-lucide-loader-2 animate-spin'
              : 'i-lucide-refresh-cw',
          ]"
        />
        {{ t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.COVER_REGENERATE') }}
      </button>
    </section>

    <section class="flex flex-col gap-2 border-t border-n-slate-5 pt-4">
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0">
          <h4 class="text-xs font-semibold text-n-slate-12">
            {{ t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.FEEDBACK_TITLE') }}
          </h4>
          <p class="text-[11px] text-n-slate-11 mt-0.5 leading-relaxed">
            {{ t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.FEEDBACK_HINT') }}
          </p>
        </div>
        <Switch
          class="mt-0.5"
          :model-value="feedbackEnabled"
          @update:model-value="emit('update:feedbackEnabled', $event)"
        />
      </div>
    </section>

    <section class="flex flex-col gap-2 border-t border-n-slate-5 pt-4">
      <h4 class="text-xs font-semibold text-n-slate-12">
        {{ t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.WIDGET_TITLE') }}
      </h4>
      <p class="text-[11px] text-n-slate-11 leading-relaxed">
        {{ t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.WIDGET_HINT') }}
      </p>
      <ComboBox
        v-model="selectedInbox"
        size="sm"
        :options="inboxOptions"
        :placeholder="
          t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.WIDGET_PLACEHOLDER')
        "
      />
    </section>
  </div>
</template>
